/**
 * Hook to register checkout store reset handler with module-quote's
 * storeChangeResetRegistry.
 *
 * When the store code changes, module-quote calls all registered handlers
 * to clear cross-module state. This hook registers the checkout reset.
 *
 * USAGE:
 * Call this hook in a component that is always mounted when checkout is available,
 * typically in the app's root layout or a shared provider.
 *
 * ```typescript
 * import { useCheckoutStoreCodeChangeRegistration } from '@kidslabs/module-checkout';
 *
 * export const AppProviders = ({children}) => {
 *     useCheckoutStoreCodeChangeRegistration();
 *     return <>{children}</>;
 * };
 * ```
 */

'use client';

import { useEffect } from 'react';
import { storeChangeResetRegistry } from '@kidslabs/module-quote';
import { checkoutStoreAPI } from '../modules/checkout/stores';

const MODULE_ID = 'checkout';

export const useCheckoutStoreCodeChangeRegistration = (): void => {
    useEffect(() => {
        storeChangeResetRegistry.register(MODULE_ID, () => {
            checkoutStoreAPI.actions.resetCheckoutStore();
        });

        return () => {
            storeChangeResetRegistry.unregister(MODULE_ID);
        };
    }, []);
};
